import {
  type ProviderInstanceId,
  type ProviderDriverKind,
  type ResolvedKeybindingsConfig,
} from "@t3tools/contracts";
import { memo, useEffect, useMemo, useState } from "react";
import type { VariantProps } from "class-variance-authority";
import { buttonVariants } from "../ui/button";
import { Popover, PopoverPopup, PopoverTrigger } from "../ui/popover";
import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";
import { cn } from "~/lib/utils";
import { ModelPickerContent } from "./ModelPickerContent";
import { ProviderInstanceIcon } from "./ProviderInstanceIcon";
import {
  ModelEsque,
  ProviderLogo,
  getTriggerDisplayModelLabel,
  getTriggerDisplayModelName,
} from "./providerIconUtils";
import { isMorrowHostedDriver, type ProviderInstanceEntry } from "../../providerInstances";
import { ComposerControl, ComposerControlChevron } from "./ComposerControl";
import { lookupMorrowModel, lookupMorrowModelBrand } from "./morrowModelCatalog";

const MORROW_AGENT_MODEL_PREFIX = "morrow-agent:";

/**
 * A friendly label for a model slug that has no live picker option yet — e.g.
 * the native agent on a fresh draft, before the provider snapshot streams in.
 * Mirrors the server's naming (`MorrowOS Agent · <Model>` / `Agent · <Model>`)
 * so the trigger reads the same on draft and persisted threads instead of
 * leaking the raw `morrow-agent:deepseek.v3.2` slug.
 */
function synthesizeTriggerModel(slug: string): ModelEsque | null {
  if (slug.startsWith(MORROW_AGENT_MODEL_PREFIX)) {
    const model = lookupMorrowModel(slug.slice(MORROW_AGENT_MODEL_PREFIX.length));
    if (!model) {
      return { slug, name: "MorrowOS Agent", shortName: "MorrowOS Agent" };
    }
    // Clean model name on the trigger (e.g. "DeepSeek V3.2"), matching the picker.
    return { slug, name: model.name, shortName: model.name };
  }
  const model = lookupMorrowModel(slug);
  return model ? { slug, name: model.name, shortName: model.shortName ?? model.name } : null;
}

export const ProviderModelPicker = memo(function ProviderModelPicker(props: {
  /**
   * The instance currently selected in the composer. Drives the trigger
   * icon, label and the default-highlighted combobox row.
   */
  activeInstanceId: ProviderInstanceId;
  model: string;
  lockedProvider: ProviderDriverKind | null;
  lockedContinuationGroupKey?: string | null;
  /** Instance entries rendered in the sidebar + used to resolve display name. */
  instanceEntries: ReadonlyArray<ProviderInstanceEntry>;
  keybindings?: ResolvedKeybindingsConfig;
  modelOptionsByInstance: ReadonlyMap<ProviderInstanceId, ReadonlyArray<ModelEsque>>;
  activeProviderIconClassName?: string;
  compact?: boolean;
  disabled?: boolean;
  terminalOpen?: boolean;
  open?: boolean;
  triggerVariant?: VariantProps<typeof buttonVariants>["variant"];
  triggerClassName?: string;
  triggerAriaLabel?: string;
  /** Composer trigger: icon only, model name stays in the tooltip / aria-label. */
  iconOnly?: boolean;
  onOpenChange?: (open: boolean) => void;
  getModelDisabledReason?: (instanceId: ProviderInstanceId, model: string) => string | null;
  onInstanceModelChange: (instanceId: ProviderInstanceId, model: string) => void;
}) {
  const [uncontrolledIsMenuOpen, setUncontrolledIsMenuOpen] = useState(false);
  const isMenuOpen = props.open ?? uncontrolledIsMenuOpen;

  // Resolve the active instance entry by exact routing key. The composer
  // resolves fallbacks before rendering this component; if the selected
  // instance disappears, do not infer a replacement from its driver kind.
  const activeEntry = useMemo(() => {
    return (
      props.instanceEntries.find((entry) => entry.instanceId === props.activeInstanceId) ?? null
    );
  }, [props.activeInstanceId, props.instanceEntries]);

  const activeInstanceId = props.activeInstanceId;
  const selectedInstanceOptions = props.modelOptionsByInstance.get(activeInstanceId) ?? [];
  // If the current slug belongs to a different instance (for example after
  // a provider switch or disable), prefer the active instance's first
  // option so the trigger icon and label stay in sync instead of showing
  // a stale foreign slug.
  const selectedModel =
    selectedInstanceOptions.find((option) => option.slug === props.model) ??
    selectedInstanceOptions[0] ??
    // No live option yet (e.g. the native agent on a fresh draft): fall back to
    // a catalog-resolved label so the trigger never shows the raw slug.
    synthesizeTriggerModel(props.model);
  const triggerTitle = selectedModel ? getTriggerDisplayModelName(selectedModel) : props.model;
  const triggerLabel = selectedModel ? getTriggerDisplayModelLabel(selectedModel) : props.model;
  const duplicateDriverCount = props.instanceEntries.filter(
    (entry) => activeEntry !== null && entry.driverKind === activeEntry.driverKind,
  ).length;
  const showInstanceBadge = Boolean(activeEntry?.accentColor) || duplicateDriverCount > 1;

  const setIsMenuOpen = (open: boolean) => {
    props.onOpenChange?.(open);
    if (props.open === undefined) {
      setUncontrolledIsMenuOpen(open);
    }
  };

  useEffect(() => {
    if (!isMenuOpen) {
      return;
    }

    const { documentElement, body } = document;
    const previousDocumentOverscrollBehavior = documentElement.style.overscrollBehavior;
    const previousBodyOverflow = body.style.overflow;
    const previousBodyPaddingRight = body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - documentElement.clientWidth;

    documentElement.style.overscrollBehavior = "contain";
    body.style.overflow = "hidden";
    if (scrollbarWidth > 0) {
      body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const shouldAllowOverlayScroll = (target: EventTarget | null) => {
      return target instanceof Element && target.closest("[data-model-picker-content]");
    };
    const preventBackgroundWheel = (event: WheelEvent) => {
      if (shouldAllowOverlayScroll(event.target)) {
        return;
      }
      event.preventDefault();
    };
    const preventBackgroundTouchMove = (event: TouchEvent) => {
      if (shouldAllowOverlayScroll(event.target)) {
        return;
      }
      event.preventDefault();
    };

    document.addEventListener("wheel", preventBackgroundWheel, { capture: true, passive: false });
    document.addEventListener("touchmove", preventBackgroundTouchMove, {
      capture: true,
      passive: false,
    });

    return () => {
      document.removeEventListener("wheel", preventBackgroundWheel, { capture: true });
      document.removeEventListener("touchmove", preventBackgroundTouchMove, { capture: true });
      documentElement.style.overscrollBehavior = previousDocumentOverscrollBehavior;
      body.style.overflow = previousBodyOverflow;
      body.style.paddingRight = previousBodyPaddingRight;
    };
  }, [isMenuOpen]);

  const handleInstanceModelChange = (instanceId: ProviderInstanceId, model: string) => {
    if (props.disabled) return;
    props.onInstanceModelChange(instanceId, model);
    setIsMenuOpen(false);
  };

  // On the native MorrowOS agent (one instance, many model brands) show the
  // SELECTED model's real company logo (OpenAI, Meta, DeepSeek, …) rather than
  // the generic agent icon, so the composer reflects what's picked.
  const modelBrand = useMemo(() => lookupMorrowModelBrand(props.model), [props.model]);
  const triggerIcon =
    modelBrand && activeEntry && isMorrowHostedDriver(activeEntry.driverKind) ? (
      <ProviderLogo
        slug={modelBrand.simpleIcon}
        name={modelBrand.displayName}
        fallback={modelBrand.icon}
        className="size-4"
      />
    ) : activeEntry ? (
      <ProviderInstanceIcon
        driverKind={activeEntry.driverKind}
        displayName={activeEntry.displayName}
        accentColor={activeEntry.accentColor}
        showBadge={showInstanceBadge}
        className="size-4"
        iconClassName={cn("size-4", props.activeProviderIconClassName)}
        indicatorBackground="var(--input)"
        badgeClassName={cn("right-[-0.125rem] bottom-[-0.125rem] h-3 min-w-3", "px-0.5 text-[7px]")}
      />
    ) : null;

  return (
    <Popover
      open={isMenuOpen}
      onOpenChange={(open) => {
        if (props.disabled) {
          setIsMenuOpen(false);
          return;
        }
        setIsMenuOpen(open);
      }}
    >
      <PopoverTrigger
        render={
          <ComposerControl
            aria-label={props.triggerAriaLabel ?? (props.iconOnly ? triggerLabel : undefined)}
            variant={props.triggerVariant ?? "ghost"}
            data-chat-provider-model-picker="true"
            className={cn(
              props.iconOnly
                ? "size-7 min-h-7 min-w-7 shrink-0 justify-center px-0"
                : cn(
                    "min-w-0 justify-between whitespace-nowrap",
                    props.compact ? "max-w-42 shrink-0" : "max-w-48 shrink sm:max-w-56",
                  ),
              props.triggerClassName,
            )}
            disabled={props.disabled}
          />
        }
      >
        {props.iconOnly ? (
          <Tooltip>
            <TooltipTrigger render={<span className="flex items-center justify-center" />}>
              {triggerIcon}
            </TooltipTrigger>
            <TooltipPopup side="top">{triggerLabel}</TooltipPopup>
          </Tooltip>
        ) : (
          <span className="flex min-w-0 flex-1 items-center gap-1.5">
            {triggerIcon}
            <Tooltip>
              <TooltipTrigger render={<span className="min-w-0 flex-1 overflow-hidden truncate" />}>
                {triggerTitle}
              </TooltipTrigger>
              <TooltipPopup side="top">{triggerLabel}</TooltipPopup>
            </Tooltip>
          </span>
        )}
        {props.iconOnly ? null : (
          <span aria-hidden="true" className="flex items-center">
            <ComposerControlChevron />
          </span>
        )}
      </PopoverTrigger>
      <PopoverPopup
        align="start"
        className="border-0 bg-transparent p-0 shadow-none before:hidden [-webkit-backdrop-filter:none]! [--viewport-inline-padding:0] [backdrop-filter:none]!"
        viewportClassName="rounded-lg !overflow-hidden p-0"
      >
        <ModelPickerContent
          activeInstanceId={activeInstanceId}
          model={props.model}
          lockedProvider={props.lockedProvider}
          lockedContinuationGroupKey={props.lockedContinuationGroupKey ?? null}
          instanceEntries={props.instanceEntries}
          {...(props.keybindings ? { keybindings: props.keybindings } : {})}
          modelOptionsByInstance={props.modelOptionsByInstance}
          terminalOpen={props.terminalOpen ?? false}
          onRequestClose={() => setIsMenuOpen(false)}
          {...(props.getModelDisabledReason
            ? { getModelDisabledReason: props.getModelDisabledReason }
            : {})}
          onInstanceModelChange={handleInstanceModelChange}
        />
      </PopoverPopup>
    </Popover>
  );
});
